import { Directive, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

/**
 * Shows a `matTooltip` only when the host element's content is truncated
 * AT REST (i.e. `text-overflow: ellipsis` is hiding part of the text
 * even when the user isn't hovering). When the content only truncates
 * BECAUSE of a hover-state layout change (e.g. the row's padding-right
 * grows on hover to make room for a caret), no tooltip is shown — the
 * truncation in that case is a side-effect of the hover, not real.
 *
 * Implementation: the directive measures overflow at REST (in `ngOnInit`
 * via `requestAnimationFrame`, then again on `window:resize`) and caches
 * the result. The cached value is what `mouseenter` consults — measuring
 * at the moment of mouseenter would already see the hover-shrunk width.
 *
 * The host element must also have `matTooltip` applied (this directive
 * injects it via `MatTooltip`).
 *
 * @example
 * <button [matTooltip]="label | translate"
 *         appOverflowTooltip=".my-label">
 *   ...
 *   <span class="my-label">{{ label | translate }}</span>
 * </button>
 *
 * Passing a CSS selector measures overflow on a descendant (typical for
 * row buttons where the label span — not the button itself — is the
 * element with `text-overflow: ellipsis`). With no selector, the host
 * element is measured directly.
 */
@Directive({
  selector: '[appOverflowTooltip]',
})
export class OverflowTooltipDirective implements OnInit, OnDestroy {
  @Input('appOverflowTooltip') overflowTarget: string = '';

  /** Quando true, NÃO re-mede o overflow em `window:resize` (e nem registra o listener). Use quando
   *  o elemento tem largura FIXA — o overflow não muda no resize — E há MUITAS instâncias na tela
   *  (ex.: célula de revisor na mini-tabela de notas, ~194×M): um listener de resize por instância
   *  disparando em rajada no arraste da janela derruba a performance, sem nenhum ganho. */
  @Input() overflowTooltipSkipResize = false;

  private measureScheduled?: ReturnType<typeof setTimeout>;
  /** Trailing-debounce da re-medição de overflow. Durante uma ANIMAÇÃO (ex.: o menu
   *  lateral expandindo, 200ms) o `ResizeObserver`/`resize` dispara a CADA FRAME, e
   *  `measureAtRest` lê `scrollWidth`/`clientWidth` = FORCED REFLOW. Com dezenas de
   *  instâncias na tela (nome do evento, autores, campos…) isso vira uma tempestade
   *  de reflow que trava a main-thread → a animação do menu "pulava" no paper-info.
   *  Só re-medimos quando o tamanho ASSENTA (~100ms após o último disparo) → zero
   *  reflow durante a animação, e o cache atualiza logo depois. Usado tanto pelo
   *  `ResizeObserver` quanto pelo listener de `window:resize`. */
  private readonly resizeHandler = (): void => {
    clearTimeout(this.measureScheduled);
    // 250ms (> a animação do menu, 200ms) DE PROPÓSITO: `measureAtRest` lê `scrollWidth`
    // = força um FLUSH de layout que renderiza TODAS as seções `content-visibility`
    // pendentes de uma vez (caro). Se disparasse durante a animação, esse flush travava
    // o meio dela. Esperando a interação assentar (250ms), o flush cai DEPOIS da animação
    // → ela roda suave (content-visibility rende preguiçoso frame a frame) e o cache de
    // overflow atualiza logo em seguida.
    this.measureScheduled = setTimeout(() => this.measureAtRest(), 250);
  };

  /** Cache do estado de overflow AT REST — atualizado em rAF inicial,
   *  em `window:resize` e via MutationObserver quando o texto muda
   *  (caso típico: pipe `translate` resolvendo depois do primeiro paint).
   *  É esse valor que `mouseenter` consulta — sem isso, mediríamos a
   *  largura já encolhida pelo hover. */
  private isOverflowAtRest = false;

  private observer?: MutationObserver;
  private resizeObserver?: ResizeObserver;

  constructor(
    private host: ElementRef<HTMLElement>,
    private tooltip: MatTooltip,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    // Start disabled so MatTooltip's own `mouseenter` handler short-
    // circuits without scheduling a show. We toggle disabled and call
    // `show()` manually in `onActivate` once we've consulted the cache.
    this.tooltip.disabled = true;
    // Mede após o primeiro paint (rAF garante que o layout estabilizou).
    requestAnimationFrame(() => {
      this.measureAtRest();
      // Observe text mutations on the target (e.g. async i18n) pra
      // remedir quando o conteúdo muda sem houver resize.
      const el = this.targetEl();
      if (el) {
        // Debounced (`resizeHandler`): mutações podem vir em rajada (ex.: seções
        // com `content-visibility` renderizando na abertura do menu × N instâncias →
        // `measureAtRest` = forced reflow por instância travava o INÍCIO da animação).
        this.observer = new MutationObserver(this.resizeHandler);
        this.observer.observe(el, { characterData: true, childList: true, subtree: true });
        // Re-mede quando a LARGURA do alvo muda (ex.: sidebar expandindo/
        // colapsando, painel resizable, breakpoints). Sem isto o cache
        // gerado no init com o layout inicial ficava obsoleto — items
        // medidos colapsados apareciam como "overflow=true" pra sempre.
        if (typeof ResizeObserver !== 'undefined') {
          this.ngZone.runOutsideAngular(() => {
            // Debounced (`resizeHandler`) — NÃO `measureAtRest` direto: durante a
            // animação do menu o RO dispara por frame × N instâncias → reflow storm.
            this.resizeObserver = new ResizeObserver(this.resizeHandler);
            this.resizeObserver.observe(el);
          });
        }
      }
    });
    // Listener de resize registrado MANUALMENTE (não via @HostListener) para poder (a) OMITI-LO
    // quando `overflowTooltipSkipResize` e (b) rodar FORA da zona — measureAtRest só atualiza um
    // cache lido no mouseenter, então não precisa disparar change detection a cada resize.
    if (!this.overflowTooltipSkipResize) {
      this.ngZone.runOutsideAngular(() => window.addEventListener('resize', this.resizeHandler));
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.resizeHandler);
    clearTimeout(this.measureScheduled);
  }

  @HostListener('mouseenter')
  @HostListener('focusin')
  onActivate(): void {
    if (this.isOverflowAtRest) {
      this.tooltip.disabled = false;
      // MatTooltip's auto-handler already ran with disabled=true and
      // bailed early; call show() directly so the tooltip respects its
      // configured `showDelay` from this point.
      this.tooltip.show();
    }
  }

  @HostListener('mouseleave')
  @HostListener('focusout')
  onDeactivate(): void {
    this.tooltip.hide();
    this.tooltip.disabled = true;
  }

  /** Mede e cacheia se o conteúdo do alvo está truncado AT REST.
   *  Chamado em init, resize e mutação de texto — momentos em que
   *  garantidamente o elemento NÃO está em estado de hover (ou era
   *  rest até a chamada). */
  private measureAtRest(): void {
    const el = this.targetEl();
    this.isOverflowAtRest = !!el && el.scrollWidth > el.clientWidth;
  }

  private targetEl(): HTMLElement | null {
    return this.overflowTarget
      ? (this.host.nativeElement.querySelector(this.overflowTarget) as HTMLElement | null)
      : this.host.nativeElement;
  }
}
